import { type ReactElement, type ReactNode, useCallback, useMemo, useState } from 'react'
import {
  type AccountStatusData,
  type AccountStatusId,
  getActionTileLabel,
  isAccountStatusId,
  isDeleted,
  isEmpty,
  isNotEmpty,
  searchIncludes,
  sortAccountStatuses,
  splitArray,
} from '@terros/common'
import { CloseButton, Combobox, Input, type InputBaseProps, Pill, PillsInput, useCombobox } from '@mantine/core'
import { useAccountStatusesMap, useTranslation } from '@front/model'
import { DisplaySelectOption } from '@components'
import { IconDisplay } from '../terros'
import { useAvailableStatuses } from './useAvailableStatuses'

type Props = {
  statusIds: AccountStatusId[]
  setStatusIds: (statusIds: AccountStatusId[]) => void
  availableStatuses?: AccountStatusId[]
  allowArchived?: boolean
  compact?: boolean
  placeholder?: string
  label?: string
  size?: string
  inputProps?: InputBaseProps
}

export const AccountStatusMultiSelect = ({
  statusIds,
  setStatusIds,
  placeholder = 'Account Status',
  availableStatuses,
  allowArchived,
  compact,
  label,
  size,
  inputProps,
}: Props): ReactElement => {
  const { t } = useTranslation()
  const stagesMap = useAccountStatusesMap({ archived: 'all' })
  const { data: statuses, isLoading } = useAvailableStatuses({ availableStatuses })
  const [search, setSearch] = useState('')

  const combobox = useCombobox({
    onDropdownOpen: () => {
      combobox.focusSearchInput()
      setSearch('')
    },
    onDropdownClose: () => combobox.resetSelectedOption(),
  })

  const allStatuses = useMemo(() => {
    if (allowArchived) return sortAccountStatuses(statuses ?? [])
    return sortAccountStatuses((statuses ?? []).filter((s) => !isDeleted(s)))
  }, [statuses, allowArchived])

  const isAllSelected = useMemo(() => {
    if (isEmpty(statusIds)) return false
    return allStatuses.every((s) => statusIds.includes(s.statusId))
  }, [statusIds, allStatuses])

  const isItemSelected = useCallback(
    (statusId: AccountStatusId) => {
      if (isAllSelected) return false
      return statusIds.includes(statusId)
    },
    [statusIds, isAllSelected]
  )

  const filtered = useMemo(() => {
    return (statuses ?? []).filter((s) => searchIncludes([s.name], search))
  }, [statuses, search])

  const comboboxOptions = useMemo((): ReactNode => {
    if (isEmpty(filtered)) return <Combobox.Empty>{t('No statuses found')}</Combobox.Empty>

    const options = []
    if (isEmpty(search)) {
      options.push(
        <Combobox.Option key='all' value='all'>
          <DisplaySelectOption label={t('All Statuses')} isSelected={isAllSelected} />
        </Combobox.Option>
      )
    }

    const { yes: archived, no: active } = splitArray(filtered, isDeleted)
    const sortedActive = sortAccountStatuses(active)
    const sortedArchived = sortAccountStatuses(archived)

    if (isNotEmpty(sortedActive)) {
      options.push(
        <Combobox.Group key='active' label={t('Account Stages')}>
          {sortedActive.map((status) => (
            <StatusSelectOption key={status.statusId} status={status} isSelected={isItemSelected(status.statusId)} />
          ))}
        </Combobox.Group>
      )
    }
    if (isNotEmpty(sortedArchived)) {
      options.push(
        <Combobox.Group key='archived' label={t('Archived Account Stages')}>
          {sortedArchived.map((status) => (
            <StatusSelectOption
              key={status.statusId}
              status={status}
              isSelected={isItemSelected(status.statusId)}
              disabled={!allowArchived}
            />
          ))}
        </Combobox.Group>
      )
    }
    return options
  }, [filtered, search, isAllSelected, isItemSelected, allowArchived, t])

  const handleOptionSelect = useCallback(
    (value: string): void => {
      if (value === 'all') {
        setStatusIds(allStatuses.map((s) => s.statusId))
        return
      }
      if (!isAccountStatusId(value)) return
      const isCurrentlyAllSelected = allStatuses.every((s) => statusIds.includes(s.statusId))
      if (isCurrentlyAllSelected) {
        setStatusIds([value])
        return
      }
      const newIds = statusIds.includes(value) ? statusIds.filter((id) => id !== value) : [...statusIds, value]
      if (isEmpty(newIds)) {
        setStatusIds(allStatuses.map((s) => s.statusId))
        return
      }
      setStatusIds(newIds)
    },
    [setStatusIds, allStatuses, statusIds]
  )

  const handleValueRemove = useCallback(
    (statusId: AccountStatusId): void => {
      const newIds = statusIds.filter((id) => id !== statusId)
      if (isEmpty(newIds)) {
        setStatusIds(allStatuses.map((s) => s.statusId))
        return
      }
      setStatusIds(newIds)
    },
    [statusIds, setStatusIds, allStatuses]
  )

  const getStatusName = useCallback(
    (statusId: AccountStatusId): string => {
      return stagesMap.get(statusId)?.name ?? statusId
    },
    [stagesMap]
  )

  const displayValue = useMemo((): ReactNode => {
    if (isEmpty(statusIds)) return <Input.Placeholder>{placeholder}</Input.Placeholder>
    if (isAllSelected) return <Input.Placeholder>{t('All Statuses')}</Input.Placeholder>
    if (compact) return `${statusIds.length} statuses`
    return statusIds.map((id) => (
      <Pill key={id} withRemoveButton onRemove={() => handleValueRemove(id)}>
        {getStatusName(id)}
      </Pill>
    ))
  }, [statusIds, isAllSelected, placeholder, t, compact, handleValueRemove, getStatusName])

  const rightSection = useMemo((): ReactNode => {
    if (isEmpty(statusIds) || isAllSelected || !compact) return <Combobox.Chevron />
    return <CloseButton size='sm' onClick={() => handleOptionSelect('all')} />
  }, [statusIds, isAllSelected, compact, handleOptionSelect])

  return (
    <Combobox store={combobox} onOptionSubmit={handleOptionSelect} position='bottom-start' width={300}>
      <Combobox.Target>
        <PillsInput
          {...inputProps}
          label={label}
          size={size}
          pointer
          onClick={() => combobox.toggleDropdown()}
          rightSection={rightSection}
          disabled={isLoading}
        >
          <Pill.Group>{displayValue}</Pill.Group>
        </PillsInput>
      </Combobox.Target>
      <Combobox.Dropdown>
        <Combobox.Search
          value={search}
          onChange={(event) => setSearch(event.currentTarget.value)}
          placeholder={t('Search statuses')}
        />
        <Combobox.Options mah={400} style={{ overflowY: 'auto' }}>
          {comboboxOptions}
        </Combobox.Options>
      </Combobox.Dropdown>
    </Combobox>
  )
}

const StatusSelectOption = ({
  status,
  isSelected,
  disabled,
}: {
  status: AccountStatusData
  isSelected: boolean
  disabled?: boolean
}): ReactElement => {
  return (
    <Combobox.Option value={status.statusId} disabled={disabled}>
      <DisplaySelectOption
        label={status.name}
        leftSection={<IconDisplay color={status.color} icon={status.icon ?? getActionTileLabel(status)} size='sm' />}
        isSelected={isSelected}
      />
    </Combobox.Option>
  )
}
